import { useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { FormProvider, useForm } from "react-hook-form";
import { intl } from "LocaleWrapper";
import { useLazyQuery, useMutation } from "@apollo/client";

import { zodResolver } from "@hookform/resolvers/zod";

import { Button, Card, PageHeader, showSnackbar } from "@cofano/cck";
import { useCoastguard } from "@cofano/coastguard2-frontend-client";
import { Tab, Tabs, Grid2 as Grid, Box } from "@mui/material";

import useTabs from "components/hooks/useTabs";
import Helmet from "components/helmet/Helmet";
import ExternalMasterdataAlert, { ExternalMasterdataWarningIcon, MasterdataEntity } from "components/masterdata/ExternalMasterdataAlert";

import { Language } from "utils";

import LocationGeneralForm from "./components/form/LocationGeneralForm";
import LocationAddressForm from "./components/form/LocationAddressForm";
import LocationContactsForm from "./components/form/LocationContactsForm";
import LocationMapForm from "./components/form/LocationMapForm";
import LocationOpeningHoursForm from "./components/form/LocationOpeningHoursForm";
import LocationRelationsForm from "./components/form/LocationRelationsForm";
import LocationQuaysForm from "./components/form/LocationQuaysForm";

import { LocationTabs } from "./utils/LocationUtils";
import { locationSchema, LocationSchemaInput, LocationSchemaOutput } from "./utils/location.schema";

import { GetLocationDocument } from "__generated__/view/locations/graphql/location.queries.generated";
import { CreateOrUpdateLocationDocument } from "__generated__/view/locations/graphql/location.mutations.generated";
import { CraneType, LocationType, LocationUserType, MooringSide } from "__generated__/enums";

const LocationCreateEdit = () => {

	const navigate = useNavigate();
	const routeLocation = useLocation();
	const { id } = useParams();

	const { instance: { forceRefreshAccessTokenAndUserDetails } } = useCoastguard();

	const stateTab = routeLocation.state?.tab;

	const { selectedTab, selectTab } = useTabs({
		defaultSelected: stateTab || LocationTabs.DETAILS,
	});

	const [ createOrEditLocation, { loading } ] = useMutation(CreateOrUpdateLocationDocument);

	const form = useForm<LocationSchemaInput, unknown, LocationSchemaOutput>({
		resolver: zodResolver(locationSchema),
		defaultValues: {
			id: null,
			description: "",
			type: LocationType.Terminal,
			locationGroups: null,
			externalId: "",
			code: "",
			abbreviation: "",
			email: "",
			telephone: "",
			preHandlingTimeMin: null,
			postHandlingTimeMin: null,
			loadingDepth: null,
			maxShipLength: null,
			maxShipWidth: null,
			maxShipDraught: null,
			notes: "",
			color: "#CCCCCC",
			isActive: true,
			isErpLocation: false,
			uabRegistration: false,

			address: {
				name1: "",
				name2: "",
				street: "",
				zipCode: "",
				town: "",
				country: "NL",
			},

			contacts: [],

			encodedPolyline: "",
			position: {
				latitude: null,
				longitude: null,
			},

			openingHours: {
				id: null,
				mondayFrom: null,
				mondayTo: null,
				tuesdayFrom: null,
				tuesdayTo: null,
				wednesdayFrom: null,
				wednesdayTo: null,
				thursdayFrom: null,
				thursdayTo: null,
				fridayFrom: null,
				fridayTo: null,
				saturdayFrom: null,
				saturdayTo: null,
				sundayFrom: null,
				sundayTo: null,
			},

			relationUsers: [],

			quays: [],
		} satisfies LocationSchemaInput,
	});

	const [ getLocation ] = useLazyQuery(GetLocationDocument, {
		onCompleted: response => {
			form.reset({
				id: response.location?.id ?? null,
				description: response.location?.description ?? "",
				type: response.location?.type ?? LocationType.Terminal,
				locationGroups: response.location?.locationGroups ?? [],
				externalId: response.location?.externalId ?? "",
				code: response.location?.code ?? "",
				abbreviation: response.location?.abbreviation ?? "",
				email: response.location?.email ?? "",
				telephone: response.location?.telephone ?? "",
				preHandlingTimeMin: response.location?.preHandlingTimeMin ?? null,
				postHandlingTimeMin: response.location?.postHandlingTimeMin ?? null,
				loadingDepth: response.location?.loadingDepth ?? null,
				maxShipLength: response.location?.maxShipLength ?? null,
				maxShipWidth: response.location?.maxShipWidth ?? null,
				maxShipDraught: response.location?.maxShipDraught ?? null,
				notes: response.location?.notes ?? "",
				color: response.location?.color ?? "#CCCCCC",
				isActive: response.location?.isActive ?? true,
				isErpLocation: response.location?.isErpLocation ?? false,
				uabRegistration: response.location?.uabRegistration ?? false,

				address: {
					name1: response.location?.address?.name1 ?? "",
					name2: response.location?.address?.name2 ?? "",
					street: response.location?.address?.street ?? "",
					zipCode: response.location?.address?.zipCode ?? "",
					town: response.location?.address?.town ?? "",
					country: response.location?.address?.country ?? "NL",
				},

				contacts: response.location?.contacts?.map(c => ({
					id: c.id,
					name: c.name ?? "",
					insertion: c.insertion ?? "",
					surName: c.surName ?? "",
					email: c.email ?? "",
					telephone: c.telephone ?? "",
					fax: c.fax ?? "",
					visibleForShip: c.visibleForShip ?? false,
				})) ?? [],

				encodedPolyline: response.location?.encodedPolyline ?? "",
				position: {
					latitude: response.location?.position?.latitude ?? null,
					longitude: response.location?.position?.longitude ?? null,
				},

				openingHours: {
					id: response.location?.openingHours?.id ?? null,
					mondayFrom: response.location?.openingHours?.mondayFrom ?? null,
					mondayTo: response.location?.openingHours?.mondayTo ?? null,
					tuesdayFrom: response.location?.openingHours?.tuesdayFrom ?? null,
					tuesdayTo: response.location?.openingHours?.tuesdayTo ?? null,
					wednesdayFrom: response.location?.openingHours?.wednesdayFrom ?? null,
					wednesdayTo: response.location?.openingHours?.wednesdayTo ?? null,
					thursdayFrom: response.location?.openingHours?.thursdayFrom ?? null,
					thursdayTo: response.location?.openingHours?.thursdayTo ?? null,
					fridayFrom: response.location?.openingHours?.fridayFrom ?? null,
					fridayTo: response.location?.openingHours?.fridayTo ?? null,
					saturdayFrom: response.location?.openingHours?.saturdayFrom ?? null,
					saturdayTo: response.location?.openingHours?.saturdayTo ?? null,
					sundayFrom: response.location?.openingHours?.sundayFrom ?? null,
					sundayTo: response.location?.openingHours?.sundayTo ?? null,
				},

				relationUsers: response.location?.relationUsers?.map(it => ({
					...it,
					id: it.id ? {
						location: it.id.location ?? null,
						customer: it.id.customer ?? null,
					} : null,
					location: it.location ?? null,
					customer: it.customer ?? null,
					type: it.type ?? LocationUserType.Owner,
				})) ?? [],

				quays: response.location?.quays?.map(quay => ({
					id: quay.id,
					name1: quay.name1 ?? "",
					length: quay.length ?? null,
					depth: quay.depth ?? null,
					latitude: quay.latitude ?? null,
					longitude: quay.longitude ?? null,
					loadingSpeedTon: quay.loadingSpeedTon ?? null,
					loadingSpeedTeu: quay.loadingSpeedTeu ?? null,
					loadingSpeedM3: quay.loadingSpeedM3 ?? null,
					loadingSpeedMove: quay.loadingSpeedMove ?? null,
					dischargingSpeedTon: quay.dischargingSpeedTon ?? null,
					dischargingSpeedTeu: quay.dischargingSpeedTeu ?? null,
					dischargingSpeedM3: quay.dischargingSpeedM3 ?? null,
					dischargingSpeedMove: quay.dischargingSpeedMove ?? null,

					bolders: quay.bolders?.map(bolder => ({
						id: bolder.id,
						description: bolder.description ?? "",
						latitude: bolder.latitude ?? null,
						longitude: bolder.longitude ?? null,
						metersToNextBolder: bolder.metersToNextBolder ?? null,
						phantomBolder: bolder.phantomBolder ?? false,
						mooringSide: bolder.mooringSide ?? MooringSide.Port,
					})) ?? [],

					cranes: quay.cranes?.map(crane => ({
						id: crane.id ?? null,
						name: crane.name ?? "",
						sequenceNumber: crane.sequenceNumber ?? null,
						craneType: crane.craneType ?? CraneType.Bulk,
						fromBolder: crane.fromBolder ? {
							id: crane.fromBolder.id ?? 0,
							description: crane.fromBolder.description ?? "",
						} : null,
						toBolder: crane.toBolder ? {
							id: crane.toBolder.id ?? 0,
							description: crane.toBolder.description ?? "",
						} : null,
						averageCraneSpeedInMovesPerHour: crane.averageCraneSpeedInMovesPerHour ?? null,
						maxHeightInMeters: crane.maxHeightInMeters ?? null,
						maxWidthInMeters: crane.maxWidthInMeters ?? null,
						outreachInContainers: crane.outreachInContainers ?? null,
						liftCapacityInTons: crane.liftCapacityInTons ?? null,

						twinningAllowed: crane.twinningAllowed ?? false,
						fullContainersAllowed: crane.fullContainersAllowed ?? false,
						canHandleOOG: crane.canHandleOOG ?? false,
						canHandleBundles: crane.canHandleBundles ?? false,

						handlingRates: crane.handlingRates?.map(handlingRate => ({
							id: handlingRate.id ?? null,
							product: handlingRate.product ?? null,
							handlingRateInTonsPerHour: handlingRate.handlingRateInTonsPerHour ?? null,
						})) ?? [],
					})) ?? [],
				})) ?? [],
			} satisfies LocationSchemaInput);
		},
	});

	useEffect(() => {
		if (id) {
			getLocation({
				variables: {
					id: Number(id),
				},
			});
		}
	}, [ id ]);

	const save = (data: LocationSchemaOutput) => {
		createOrEditLocation({
			variables: {
				input: {
					id: data.id ?? undefined,
					description: data.description,
					type: data.type,
					locationGroups: data.locationGroups ?? [],
					externalId: data.externalId || null,
					code: data.code || null,
					abbreviation: data.abbreviation || null,
					email: data.email || null,
					telephone: data.telephone || null,
					preHandlingTimeMin: data.preHandlingTimeMin ?? undefined,
					postHandlingTimeMin: data.postHandlingTimeMin ?? undefined,
					loadingDepth: data.loadingDepth ?? undefined,
					maxShipLength: data.maxShipLength ?? undefined,
					maxShipWidth: data.maxShipWidth ?? undefined,
					maxShipDraught: data.maxShipDraught ?? undefined,
					notes: data.notes,
					color: data.color || "#CCCCCC",
					isActive: data.isActive,
					isErpLocation: data.isErpLocation,
					uabRegistration: data.uabRegistration ?? false,

					address: {
						name1: data.address.name1 || null,
						name2: data.address.name2 || null,
						street: data.address.street || null,
						zipCode: data.address.zipCode || null,
						town: data.address.town || null,
						country: data.address.country || null,
					},

					contacts: data.contacts.map((c) => ({
						id: c.id,
						name: c.name || null,
						insertion: c.insertion || null,
						surName: c.surName || null,
						email: c.email || null,
						telephone: c.telephone || null,
						fax: c.fax || null,
						visibleForShip: c.visibleForShip || false,
					})),

					encodedPolyline: data.encodedPolyline || null,
					position: {
						latitude: data.position.latitude,
						longitude: data.position.longitude,
					},

					openingHours: {
						id: data.openingHours.id ?? null,
						mondayFrom: data.openingHours.mondayFrom ?? null,
						mondayTo: data.openingHours.mondayTo ?? null,
						tuesdayFrom: data.openingHours.tuesdayFrom ?? null,
						tuesdayTo: data.openingHours.tuesdayTo ?? null,
						wednesdayFrom: data.openingHours.wednesdayFrom ?? null,
						wednesdayTo: data.openingHours.wednesdayTo ?? null,
						thursdayFrom: data.openingHours.thursdayFrom ?? null,
						thursdayTo: data.openingHours.thursdayTo ?? null,
						fridayFrom: data.openingHours.fridayFrom ?? null,
						fridayTo: data.openingHours.fridayTo ?? null,
						saturdayFrom: data.openingHours.saturdayFrom ?? null,
						saturdayTo: data.openingHours.saturdayTo ?? null,
						sundayFrom: data.openingHours.sundayFrom ?? null,
						sundayTo: data.openingHours.sundayTo ?? null,
					},

					relationUsers: data.relationUsers.map(relationUser => ({
						...relationUser,
						customer: {
							id: relationUser.customer.id,
						},
					})),

					quays: data.quays.map(quay => ({
						id: quay.id,
						name1: quay.name1,
						length: quay.length,
						depth: quay.depth,
						latitude: quay.latitude ?? 0,
						longitude: quay.longitude ?? 0,
						loadingSpeedTon: quay.loadingSpeedTon,
						loadingSpeedTeu: quay.loadingSpeedTeu,
						loadingSpeedM3: quay.loadingSpeedM3,
						loadingSpeedMove: quay.loadingSpeedMove,
						dischargingSpeedTon: quay.dischargingSpeedTon,
						dischargingSpeedTeu: quay.dischargingSpeedTeu,
						dischargingSpeedM3: quay.dischargingSpeedM3,
						dischargingSpeedMove: quay.dischargingSpeedMove,
						bolders: quay.bolders.map(bolder => ({
							id: bolder.id,
							description: bolder.description,
							latitude: bolder.latitude,
							longitude: bolder.longitude,
							metersToNextBolder: bolder.metersToNextBolder ?? 0,
							phantomBolder: bolder.phantomBolder,
							mooringSide: bolder.mooringSide,
						})),

						cranes: quay.cranes.map(crane => {
							if (crane.craneType === CraneType.Bulk) {
								return {
									id: crane.id,
									name: crane.name,
									sequenceNumber: crane.sequenceNumber ?? 0,
									craneType: crane.craneType,
									fromBolder: crane.fromBolder ? { id: crane.fromBolder.id } : null,
									toBolder: crane.toBolder ? { id: crane.toBolder.id } : null,
									handlingRates: crane.handlingRates?.map(handlingRate => ({
										id: handlingRate.id,
										product: {
											id: handlingRate.product.id,
										},
										handlingRateInTonsPerHour: handlingRate.handlingRateInTonsPerHour,
									})) || [],
								};
							} else {
								return {
									id: crane.id,
									name: crane.name,
									sequenceNumber: crane.sequenceNumber ?? 0,
									craneType: crane.craneType,
									fromBolder: crane.fromBolder ? { id: crane.fromBolder.id } : null,
									toBolder: crane.toBolder ? { id: crane.toBolder.id } : null,
									averageCraneSpeedInMovesPerHour: crane.averageCraneSpeedInMovesPerHour ?? 0,
									maxHeightInMeters: crane.maxHeightInMeters ?? 0,
									maxWidthInMeters: crane.maxWidthInMeters ?? 0,
									outreachInContainers: crane.outreachInContainers ?? 0,
									liftCapacityInTons: crane.liftCapacityInTons ?? 0,
									twinningAllowed: crane.twinningAllowed,
									fullContainersAllowed: crane.fullContainersAllowed,
									canHandleOOG: crane.canHandleOOG,
									canHandleBundles: crane.canHandleBundles,
									handlingRates: [],
								};
							}
						}),
					})),
				},
			},
		}).then(async (response) => {
			if (response.data) {
				showSnackbar(intl.formatMessage({ id: "SUCCESSFUL_SAVED" }), { variant: "success" });
				navigate(`/view/locations/${response.data.createOrUpdateLocation?.id}/details`, { state: { tab: selectedTab } });

				//update the user session because location favorites may have changed
				await forceRefreshAccessTokenAndUserDetails();
			}
		});
	};

	const hasDetailTabErrors = () => (form.formState.errors.description || form.formState.errors.locationGroups);
	const hasContactTabErrors = () => !!form.formState.errors.contacts;
	const hasRelationTabErrors = () => !!(form.formState.errors.relationUsers);
	const hasQuayTabErrors = () => !!(form.formState.errors.quays);

	const masterdataEntity = form.watch("type") === LocationType.Port
		? MasterdataEntity.LocationPort
		: form.watch("type") === LocationType.Terminal
			? MasterdataEntity.LocationTerminal
			: MasterdataEntity.Location;

	return (
		<FormProvider {...form}>
			<div className="location-create-edit">
				<form onSubmit={form.handleSubmit(save)}>
					<PageHeader>
						<PageHeader.Title>
							{form.watch("description") || Language("LOCATION")}
							<Helmet title={form.watch("description") || intl.formatMessage({ id: "LOCATION" })} />
						</PageHeader.Title>

						<PageHeader.Actions>
							<Button onClick={() => navigate(id ? `/view/locations/${id}/details` : "/view/locations", { state: { tab: selectedTab } })} variant="outlined">
								{Language("CANCEL")}
							</Button>

							<Button loading={loading} type="submit">
								{Language("SAVE")}
							</Button>

							<ExternalMasterdataWarningIcon entity={masterdataEntity} />
						</PageHeader.Actions>
					</PageHeader>

					<ExternalMasterdataAlert entity={masterdataEntity} externalId={form.watch("externalId")} />

					<Box mb={2}>
						<Card disablePadding>
							<Tabs
								value={selectedTab}
								onChange={(_e, value) => selectTab(value)}
								indicatorColor="primary"
								variant="scrollable">
								<Tab
									value={LocationTabs.DETAILS}
									label={Language("DETAILS")}
									sx={hasDetailTabErrors() ? { color: (theme) => theme.palette.error.main } : undefined} />

								<Tab
									value={LocationTabs.CONTACTS}
									label={intl.formatMessage({ id: "CONTACTS" })}
									sx={hasContactTabErrors() ? { color: (theme) => theme.palette.error.main } : undefined} />

								<Tab
									value={LocationTabs.MAP}
									label={Language("LOCATION_MAP")} />

								<Tab
									value={LocationTabs.OPENING_HOURS}
									label={Language("LOCATION_OPENING_HOURS")} />

								<Tab
									value={LocationTabs.RELATIONS}
									label={Language("RELATIONS")}
									sx={hasRelationTabErrors() ? { color: (theme) => theme.palette.error.main } : undefined} />

								<Tab
									value={LocationTabs.QUAYS}
									label={Language("QUAYS")}
									sx={hasQuayTabErrors() ? { color: (theme) => theme.palette.error.main } : undefined} />
							</Tabs>
						</Card>
					</Box>

					{selectedTab === LocationTabs.DETAILS && (
						<Grid spacing={3} container>
							<Grid size={{ lg: 4, md: 4, xs: 12 }}>
								<Card title={intl.formatMessage({ id: "DETAILS" })}>
									<LocationGeneralForm />
								</Card>
							</Grid>

							<Grid size={{ lg: 4, md: 4, xs: 12 }}>
								<Card title={intl.formatMessage({ id: "ADDRESS" })}>
									<LocationAddressForm />
								</Card>
							</Grid>
						</Grid>
					)}

					{selectedTab === LocationTabs.CONTACTS && (
						<Card title={intl.formatMessage({ id: "CONTACTS" })} disablePadding>
							<LocationContactsForm />
						</Card>
					)}

					{selectedTab === LocationTabs.MAP && (
						<LocationMapForm key={form.getValues("id") || 0} />
					)}

					{selectedTab === LocationTabs.OPENING_HOURS && (
						<Grid spacing={3} container>
							<Grid size={{ lg: 6, md: 6, xs: 12 }}>
								<Card>
									<LocationOpeningHoursForm />
								</Card>
							</Grid>
						</Grid>
					)}

					{selectedTab === LocationTabs.RELATIONS && (
						<Grid spacing={3} container>
							<Grid size={{ lg: 6, md: 6, xs: 12 }}>
								<Card disablePadding>
									<LocationRelationsForm />
								</Card>
							</Grid>
						</Grid>
					)}

					{selectedTab === LocationTabs.QUAYS && (
						<LocationQuaysForm />
					)}
				</form>
			</div>
		</FormProvider>
	);
};

export default LocationCreateEdit;
